import { adminRecords } from "./adminRecords";
import React, { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import Auth from "../Auth";


function parseQuillDelta(rawContent) {
  if (rawContent == null) {
    return null;
  }

  let parsed = rawContent;
  if (typeof rawContent === "string") {
    const trimmed = rawContent.trim();
    if (!trimmed) {
      return null;
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  if (parsed && Array.isArray(parsed.ops)) {
    return parsed;
  }

  return null;
}

function getProseMirrorPlainText(json) {
  if (!json?.default?.content?.length) {
    return "";
  }

  const extractText = (nodes) => {
    let text = "";
    for (const node of nodes) {
      if (node.type === "text") {
        text += node.text || "";
      } else if (node.content && Array.isArray(node.content)) {
        text += extractText(node.content);
      }
      if (node.type === "paragraph" || node.type === "heading") {
        text += "\n";
      }
    }
    return text;
  };

  return extractText(json.default.content).trim();
}

function Document(props) {
  const [document, setDocument] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const { id } = useParams();

  useEffect(() => {
    adminRecords.collection("documents")
      .doc(id)
      .get()
      .then(async (ref) => {
        await props.userCache.fetchIds([ref.get("userId")]);
        const theUser = props.userCache.getUser(ref.get("userId"));

        setDocument(ref);
        setUser(theUser);
        setLoading(false);
      });
  }, [id, props.userCache]);

  return (
    <div id="document">
      <div className="inner">
        <h1>Document ID: {id}</h1>

        {!loading && user && (
          <>
            <h2>
              From <a href={`/admin/user/${user.email}`}>{user.email}</a>{" "}
            </h2>

            <hr />
            <DocView document={document} documentId={id} />
          </>
        )}
        <div
          style={{ display: "none" }}
          id="editor"
        ></div>
      </div>
    </div>
  );
}

function DocView({ document, documentId }) {
  const [viewState, setViewState] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;

    async function loadDocumentContent() {
      setViewState({ status: "loading" });

      const quillDelta = parseQuillDelta(document?.get?.("content"));
      if (quillDelta) {
        try {
          const { default: Quill } = await import("quill");
          if (cancelled) {
            return;
          }

          const quill = new Quill("#editor");
          quill.setContents(quillDelta);

          const editorEl = document.getElementById("editor")?.getElementsByClassName("ql-editor")[0];
          if (!editorEl || cancelled) {
            return;
          }

          setViewState({ status: "html", html: editorEl.innerHTML });
          return;
        } catch (error) {
          console.error("Failed to render legacy Quill document", error);
          if (!cancelled) {
            setViewState({ status: "error", message: "Failed to render legacy document content." });
          }
          return;
        }
      }

      try {
        const response = await Auth.fetchWithIdToken("/api/admin/diagnose-document", { documentId, includeRawJson: true });
        if (cancelled) {
          return;
        }

        if (response.empty) {
          setViewState({ status: "empty" });
          return;
        }

        if (!response.success) {
          setViewState({
            status: "error",
            message: response.error || "Document content is not available.",
          });
          return;
        }

        const text = getProseMirrorPlainText({ default: response.rawJson });
        if (!text) {
          setViewState({ status: "empty" });
          return;
        }

        setViewState({ status: "text", text });
      } catch (error) {
        console.error("Failed to load document content", error);
        if (!cancelled) {
          setViewState({ status: "error", message: "Failed to load document content." });
        }
      }
    }

    loadDocumentContent();

    return () => {
      cancelled = true;
    };
  }, [document, documentId]);

  if (viewState.status === "loading") {
    return <div className="content">Loading document content…</div>;
  }

  if (viewState.status === "empty") {
    return <div className="content">This document has no content.</div>;
  }

  if (viewState.status === "error") {
    return <div className="content">{viewState.message}</div>;
  }

  if (viewState.status === "text") {
    return <div className="content whitespace-pre-wrap">{viewState.text}</div>;
  }

  return (
    <div
      className="content"
      dangerouslySetInnerHTML={{ __html: viewState.html }}
    />
  );
}

export default Document;
